import { useEffect, useReducer, useRef, useState } from "react";
import {
  localeFromUrl,
  persistLocale,
  resolveInitialLocale,
  withLocale,
} from "../i18n/locale";
import { appReducer, type AppAction } from "./reducer";
import { parseUrlState, serializeUrlState } from "./urlState";
import type {
  AppState,
  CookingLocationId,
  FoodTypeId,
  Locale,
  SelectionState,
  Suggestion,
} from "../domain/types";
import { Builder } from "../features/builder/Builder";
import { RecipeDetail } from "../features/recipe-detail/RecipeDetail";
import { ShareFeedback, type ShareFeedbackState } from "./ShareFeedback";

const initial = parseUrlState(window.location.search);
const initialLocale = resolveInitialLocale(window.location.search);

function buildUrl(state: AppState, locale: Locale): string {
  const canonical = serializeUrlState(state);
  const queryIndex = canonical.indexOf("?");
  const path = queryIndex === -1 ? canonical : canonical.slice(0, queryIndex);
  const existingQuery = queryIndex === -1 ? "" : canonical.slice(queryIndex);
  return `${path}${withLocale(existingQuery, locale)}`;
}

function scrollImmediately(top: number) {
  document.documentElement.scrollTop = top;
  document.body.scrollTop = top;
}

function replaceNonCanonicalUrl(state: AppState, locale: Locale) {
  const canonical = buildUrl(state, locale);
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current !== canonical) {
    window.history.replaceState(window.history.state, "", canonical);
  }
}

export function App() {
  const [state, dispatch] = useReducer(appReducer, initial.state);
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [statusMessage, setStatusMessage] = useState<string | null>(
    initial.message,
  );
  const [shareFeedback, setShareFeedback] = useState<ShareFeedbackState | null>(
    null,
  );
  const [returnFocusRecipeId, setReturnFocusRecipeId] = useState<string | null>(
    initial.state.recipeId,
  );
  const builderScrollY = useRef<number | null>(null);
  const builderReturnState = useRef<AppState | null>(null);
  const currentState = useRef(state);
  const currentLocale = useRef(locale);

  useEffect(() => {
    replaceNonCanonicalUrl(initial.state, initialLocale);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    let scrollY: number | null = null;
    if (state.recipeId) {
      scrollY = 0;
    } else if (builderScrollY.current !== null) {
      scrollY = builderScrollY.current;
      builderScrollY.current = null;
    }
    if (scrollY === null) return;
    const frame = window.requestAnimationFrame(() =>
      scrollImmediately(scrollY),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [state.recipeId]);

  useEffect(() => {
    function onPopState() {
      const parsed = parseUrlState(window.location.search);
      const nextLocale = localeFromUrl(window.location.search) ?? "en";
      replaceNonCanonicalUrl(parsed.state, nextLocale);
      if (parsed.state.recipeId && !currentState.current.recipeId) {
        builderReturnState.current = currentState.current;
        if (builderScrollY.current === null)
          builderScrollY.current = window.scrollY;
      }
      currentState.current = parsed.state;
      currentLocale.current = nextLocale;
      dispatch({ type: "replace", state: parsed.state });
      setLocale(nextLocale);
      setStatusMessage(parsed.message);
      setShareFeedback(null);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function commit(action: AppAction, mode: "push" | "replace" = "push") {
    const next = appReducer(state, action);
    window.history[mode === "push" ? "pushState" : "replaceState"](
      {},
      "",
      buildUrl(next, currentLocale.current),
    );
    currentState.current = next;
    dispatch({ type: "replace", state: next });
    setStatusMessage(null);
    setShareFeedback(null);
  }

  function changeLocale(next: Locale) {
    persistLocale(next);
    currentLocale.current = next;
    window.history.replaceState({}, "", buildUrl(currentState.current, next));
    setLocale(next);
  }

  async function share() {
    const url = window.location.href;
    const usedNativeShare = typeof navigator.share === "function";
    setShareFeedback(null);
    try {
      if (usedNativeShare) await navigator.share({ title: "FlavorCraft", url });
      else if (typeof navigator.clipboard?.writeText === "function")
        await navigator.clipboard.writeText(url);
      else throw new Error("Clipboard unavailable");
      setShareFeedback({
        message: usedNativeShare ? "Share sheet opened" : "Link copied",
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setShareFeedback({
        message: "Select and copy this link to share this view",
        url,
      });
    }
  }

  function openRecipe(id: string) {
    setReturnFocusRecipeId(id);
    builderReturnState.current = state;
    builderScrollY.current = window.scrollY;
    commit({ type: "open-recipe", id });
  }

  function closeRecipe() {
    setReturnFocusRecipeId(state.recipeId);
    const returnState = builderReturnState.current;
    builderReturnState.current = null;
    if (returnState) {
      commit({
        type: "replace",
        state: { ...returnState, recipeId: null, swaps: {} },
      });
    } else {
      commit({ type: "close-recipe" });
    }
  }

  function applySuggestion(suggestion: Suggestion) {
    if (suggestion.kind === "food-type")
      commit({
        type: "toggle-food-type",
        id: suggestion.id as FoodTypeId,
      });
    else if (suggestion.kind === "cooking-location")
      commit({
        type: "toggle-cooking-location",
        id: suggestion.id as CookingLocationId,
      });
    else commit({ type: "toggle-ingredient", id: suggestion.id });
  }

  function recover(selection: SelectionState, recipeId?: string) {
    if (recipeId) {
      setReturnFocusRecipeId(recipeId);
      builderReturnState.current = state;
      builderScrollY.current = window.scrollY;
    }
    commit({
      type: "replace",
      state: { ...selection, recipeId: recipeId ?? null, swaps: {} },
    });
  }

  return (
    <>
      {state.recipeId ? (
        <RecipeDetail
          state={state}
          locale={locale}
          onLocaleChange={changeLocale}
          onBack={closeRecipe}
          onSwap={(slotId, ingredientId) =>
            commit({ type: "set-swap", slotId, ingredientId })
          }
          onShare={share}
        />
      ) : (
        <Builder
          state={state}
          locale={locale}
          onLocaleChange={changeLocale}
          statusMessage={statusMessage}
          returnFocusRecipeId={returnFocusRecipeId}
          onFoodType={(id) => commit({ type: "toggle-food-type", id })}
          onCookingLocation={(id) =>
            commit({ type: "toggle-cooking-location", id })
          }
          onIngredient={(id) => commit({ type: "toggle-ingredient", id })}
          onVeganOnly={(value) => commit({ type: "set-vegan-only", value })}
          onClear={() => commit({ type: "clear" })}
          onOpenRecipe={openRecipe}
          onShare={share}
          onRecover={recover}
          onSuggestion={applySuggestion}
        />
      )}
      <ShareFeedback
        feedback={shareFeedback}
        onDismiss={() => setShareFeedback(null)}
      />
    </>
  );
}
